import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, X, ChevronDown, ChevronUp, Check, Link as LinkIcon } from "lucide-react";

export default function ClientAddOnsPanel({ clientId }) {
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState([]); // client_addons rows
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const [{ data: cat }, { data: cli }] = await Promise.all([
        api.get("/addons/catalog"),
        api.get(`/addons/client/${clientId}`),
      ]);
      setCatalog((cat.addons || []).filter((a) => a.active));
      setActive(cli.addons || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const activeIds = useMemo(
    () => new Set(active.filter((a) => a.status === "active").map((a) => a.addon_id)),
    [active]
  );

  const activate = async (addon) => {
    setBusy((b) => ({ ...b, [addon.id]: true }));
    try {
      await api.post(`/addons/client/${clientId}/${addon.id}`, { notes: "" });
      toast.success(`Activated: ${addon.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [addon.id]: false }));
    }
  };

  const deactivate = async (addon) => {
    if (!window.confirm(`Cancel "${addon.name}" for this client?`)) return;
    setBusy((b) => ({ ...b, [addon.id]: true }));
    try {
      await api.delete(`/addons/client/${clientId}/${addon.id}`);
      toast.success(`Cancelled: ${addon.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [addon.id]: false }));
    }
  };

  const activeTotal = useMemo(
    () =>
      active
        .filter((a) => a.status === "active")
        .reduce((s, a) => s + (a.price || 0), 0),
    [active]
  );

  const copyPortalLink = async () => {
    try {
      const { data } = await api.get(`/portal/link/${clientId}`);
      const url = `${window.location.origin}${data.url_suffix}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Portal link copied — expires in ${data.expires_in_days} days`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 p-6">
        <div className="text-neutral-500 text-sm">Loading add-ons…</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid="client-addons-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#E24E1B]" /> Add-Ons
          </div>
          <div className="text-sm text-neutral-600">
            Activate revenue-expanding features. Website add-ons auto-regenerate the preview site.
          </div>
        </div>
        <div className="text-right">
          <div className="overline text-neutral-500">Add-on MRR</div>
          <div className="font-mono font-bold text-lg text-[#002FA7]">
            +${activeTotal.toLocaleString()}
            <span className="text-xs text-neutral-500 ml-1">/mo</span>
          </div>
          <button
            onClick={copyPortalLink}
            data-testid="client-portal-link-btn"
            className="mt-2 text-xs text-[#0F2340] hover:text-[#E24E1B] flex items-center gap-1 ml-auto"
          >
            <LinkIcon className="w-3 h-3" /> Copy portal link
          </button>
        </div>
      </div>

      {/* Active add-ons summary */}
      {activeIds.size > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 font-semibold">
            Active ({activeIds.size})
          </div>
          <div className="flex flex-wrap gap-2">
            {active
              .filter((a) => a.status === "active")
              .map((a) => {
                const err = a.stripe_sync_error;
                const stripeOk = a.stripe_subscription_item_id;
                const badgeColor = err === "no_subscription"
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : err
                    ? "bg-red-50 border-red-200 text-red-800"
                    : "bg-emerald-50 border-emerald-200 text-emerald-800";
                const tooltip = err === "no_subscription"
                  ? "Local only — client has no active Stripe subscription"
                  : err
                    ? `Stripe sync error: ${err}`
                    : stripeOk
                      ? "Live on Stripe subscription"
                      : "Local tracking";
                return (
                  <div
                    key={a.addon_id}
                    data-testid={`active-addon-${a.addon_id}`}
                    title={tooltip}
                    className={`flex items-center gap-2 px-3 py-1.5 border ${badgeColor}`}
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span className="text-sm font-medium">{a.addon_name}</span>
                    <span className="text-xs font-mono">${a.price}/mo</span>
                    {stripeOk && <span className="text-[10px] font-mono opacity-70">· stripe</span>}
                    {err === "no_subscription" && <span className="text-[10px] font-mono opacity-70">· local</span>}
                    {err && err !== "no_subscription" && <span className="text-[10px] font-mono">· ⚠</span>}
                    <button
                      onClick={() => deactivate({ id: a.addon_id, name: a.addon_name })}
                      disabled={busy[a.addon_id]}
                      className="ml-1 hover:opacity-60 transition"
                      aria-label="Cancel"
                      data-testid={`cancel-addon-${a.addon_id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Available add-ons toggle */}
      <button
        onClick={() => setExpanded((e) => !e)}
        data-testid="toggle-addon-catalog"
        className="w-full flex items-center justify-between text-sm font-semibold text-neutral-700 hover:text-neutral-900 border-t border-neutral-100 pt-4"
      >
        <span>Browse available add-ons ({catalog.length - activeIds.size} to add)</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {catalog
            .filter((a) => !activeIds.has(a.id))
            .map((a) => (
              <div
                key={a.id}
                data-testid={`available-addon-${a.id}`}
                className="border border-neutral-200 p-3 flex flex-col hover:border-neutral-400 transition"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm">{a.name}</div>
                  <span className="text-xs font-mono text-neutral-600 whitespace-nowrap">
                    ${a.price}/mo
                    {a.setup_fee > 0 && <span className="text-neutral-400"> · ${a.setup_fee} setup</span>}
                  </span>
                </div>
                <div className="text-xs text-neutral-500 mb-3 flex-1">{a.tagline}</div>
                <button
                  onClick={() => activate(a)}
                  disabled={busy[a.id]}
                  className="lf-btn-outline text-xs py-1.5 flex items-center justify-center gap-1 hover:bg-[#0F2340] hover:text-white hover:border-[#0F2340]"
                  data-testid={`activate-addon-${a.id}`}
                >
                  <Plus className="w-3 h-3" /> {busy[a.id] ? "Activating…" : "Activate"}
                </button>
              </div>
            ))}
          {catalog.filter((a) => !activeIds.has(a.id)).length === 0 && (
            <div className="col-span-full text-center py-6 text-sm text-neutral-500">
              🎉 All available add-ons are activated for this client.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
